import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap } from 'rxjs/operators';
import { B2BUserConnector } from '../../../../features/organization-base/connectors';
import { LoginLogoutActionTypes } from '../../../auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../global-message';
import { HttpResponseStatus } from '../../../global-message/models/response-status.model';
import { GlobalMessageActions } from '../../../global-message/store';
import { OrderCard, OrgUnitUserPage } from '../../../model';
import { CurrenciesActionTypes } from '../../../site-context/store/actions/currencies.action';
import { LanguagesActionTypes } from '../../../site-context/store/actions/languages.action';
import { normalizeHttpError, withdrawOn } from '../../../util';
import { AppActions } from '../../../util/store/actions';
import { OrderCardsAdminConnector } from '../../connectors/order-cards-admin-connector';
import { UserActions } from '../actions';

@Injectable()
export class OrderCardsAdminEffects {
  private contextChange$ = this.actions$.pipe(
    ofType(CurrenciesActionTypes.CurrencyChange, LanguagesActionTypes.LanguageChange, LoginLogoutActionTypes.Logout)
  );
  private logoutOnlyContextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  loadOrderCardAdmin$: Observable<UserActions.LoadOrderCardAdminSuccess | UserActions.LoadOrderCardAdminFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.LoadOrderCardAdmin),
      switchMap((action: UserActions.LoadOrderCardAdmin) =>
        this.connector.getOrderCard(action.userId, action.code).pipe(
          map((data) => new UserActions.LoadOrderCardAdminSuccess(action.code, data)),
          catchError((error) => of(new UserActions.LoadOrderCardAdminFail(action.code, normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForLoadOrderCardAdmin$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.LoadOrderCardAdminFail),
      map((action: UserActions.LoadOrderCardAdminFail) => {
        if (!isErrorGloballyHandled(action.payload.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'orderCards.loadOrderCardFailure_message',
              params: {
                orderCardCode: action.payload.code,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  getOrderCardsAdmin$: Observable<UserActions.SearchOrderCardsAdminSuccess | UserActions.SearchOrderCardsAdminFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.OrderCardsAdminActionTypes.SearchOrderCardsAdmin),
        map((action: UserActions.SearchOrderCardsAdmin) => action.payload),
        switchMap((payload) =>
          this.connector.getOrderCards(payload.userId, payload.searchParams).pipe(
            map((data) => new UserActions.SearchOrderCardsAdminSuccess({ ...payload, searchResult: data })),
            catchError((error) =>
              of(
                new UserActions.SearchOrderCardsAdminFail({
                  ...payload,
                  error: normalizeHttpError(error),
                })
              )
            )
          )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  deleteOrderCardAdmin$: Observable<UserActions.DeleteOrderCardAdminSuccess | UserActions.DeleteOrderCardAdminFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.OrderCardsAdminActionTypes.DeleteOrderCardAdmin),
        map((action: UserActions.DeleteOrderCardAdmin) => action.payload),
        switchMap((payload) =>
          this.connector.deleteOrderCard(payload.userId, payload.code).pipe(
            map((_data) => new UserActions.DeleteOrderCardAdminSuccess(payload)),
            catchError((error) =>
              of(
                new UserActions.DeleteOrderCardAdminFail({
                  ...payload,
                  error: normalizeHttpError(error),
                })
              )
            )
          )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  searchOrderCardsAdminAfterDelete$: Observable<UserActions.SearchOrderCardsAdmin> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.DeleteOrderCardAdminSuccess),
      map((action: UserActions.DeleteOrderCardAdminSuccess) => action.payload),
      switchMap((payload) => of(new UserActions.SearchOrderCardsAdmin(payload)))
    )
  );

  handleFailureForDeleteOrderCardAdmin$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.DeleteOrderCardAdminFail),
      map((action: UserActions.DeleteOrderCardAdminFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'orderCards.removeOrderCardFailure_message',
              params: {
                orderCardCode: action.payload.code,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  createOrderCardAdmin$: Observable<
    UserActions.CreateOrderCardAdminSuccess | UserActions.CreateOrderCardAdminFail | GlobalMessageActions.AddMessage
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.CreateOrderCardAdmin),
      map((action: UserActions.CreateOrderCardAdmin) => action.payload),
      switchMap((payload) =>
        this.connector.createOrderCard(payload.userId, payload.orderCardParams).pipe(
          concatMap((orderCard: OrderCard) => [
            new UserActions.CreateOrderCardAdminSuccess({ ...payload, orderCard }),
            new GlobalMessageActions.AddMessage({
              text: { key: 'orderCards.createOrderCardSuccess_message' },
              type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
            }),
          ]),
          catchError((error) =>
            of(
              new UserActions.CreateOrderCardAdminFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForCreateOrderCardAdmin$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.CreateOrderCardAdminFail),
      map((action: UserActions.CreateOrderCardAdminFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'orderCards.createOrderCardFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  updateOrderCard$: Observable<UserActions.UpdateOrderCardAdminSuccess | UserActions.UpdateOrderCardAdminFail> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.OrderCardsAdminActionTypes.UpdateOrderCardAdmin),
        map((action: UserActions.UpdateOrderCardAdmin) => action.payload),
        switchMap((payload) =>
          this.connector.updateOrderCard(payload.userId, payload.code, payload.orderCardParams).pipe(
            map((orderCard: OrderCard) => new UserActions.UpdateOrderCardAdminSuccess({ ...payload, orderCard })),
            catchError((error) =>
              of(
                new UserActions.UpdateOrderCardAdminFail({
                  ...payload,
                  error: normalizeHttpError(error),
                })
              )
            )
          )
        ),
        withdrawOn(this.logoutOnlyContextChange$)
      )
  );

  handleFailureForUpdateOrderCard$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.UpdateOrderCardAdminFail),
      map((action: UserActions.UpdateOrderCardAdminFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          if (action.payload.error?.status === HttpResponseStatus.NOT_FOUND) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderCards.orderCardNotFound_message',
                params: {
                  orderCardCode: action.payload.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderCards.updateOrderCardFailure_message',
                params: {
                  orderCardCode: action.payload.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          }
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  updateOrderCardStatus$: Observable<UserActions.UpdateOrderCardAdminStatusSuccess | UserActions.UpdateOrderCardAdminStatusFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.OrderCardsAdminActionTypes.UpdateOrderCardAdminStatus),
        map((action: UserActions.UpdateOrderCardAdminStatus) => action.payload),
        switchMap((payload) =>
          this.connector.updateOrderCardAdminStatus(payload.userId, payload.code, payload.status).pipe(
            map((orderCard: OrderCard) => new UserActions.UpdateOrderCardAdminStatusSuccess({ ...payload, orderCard })),
            catchError((error) =>
              of(
                new UserActions.UpdateOrderCardAdminStatusFail({
                  ...payload,
                  error: normalizeHttpError(error),
                })
              )
            )
          )
        ),
        withdrawOn(this.logoutOnlyContextChange$)
      )
    );

  handleFailureForUpdateOrderCardStatus$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.UpdateOrderCardAdminStatusFail),
      map((action: UserActions.UpdateOrderCardAdminStatusFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          if (action.payload.error?.status === HttpResponseStatus.NOT_FOUND) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderCards.orderCardNotFound_message',
                params: {
                  orderCardCode: action.payload.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderCards.updateOrderCardStatusFailure_message',
                params: {
                  orderCardCode: action.payload.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          }
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  addOrderCardPrincipal$: Observable<UserActions.AddOrderCardAdminPrincipalSuccess | UserActions.AddOrderCardAdminPrincipalFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.OrderCardsAdminActionTypes.AddOrderCardAdminPrincipal),
        switchMap((action: UserActions.AddOrderCardAdminPrincipal) =>
          this.connector.addOrderCardPrincipal(action.userId, action.code, action.principal).pipe(
            map((orderCard: OrderCard) => new UserActions.AddOrderCardAdminPrincipalSuccess(action.code, orderCard)),
            catchError((error) => of(new UserActions.AddOrderCardAdminPrincipalFail(action.code, normalizeHttpError(error))))
          )
        ),
        withdrawOn(this.logoutOnlyContextChange$)
      )
    );

  handleFailureForAddOrderCardPrincipal$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.AddOrderCardAdminPrincipalFail),
      map((action: UserActions.AddOrderCardAdminPrincipalFail) => {
        if (!isErrorGloballyHandled(action.error?.status)) {
          if (action.error?.status === HttpResponseStatus.NOT_FOUND) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderCards.orderCardNotFound_message',
                params: {
                  orderCardCode: action.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderCards.addOrderCardPrincipalFailure_message',
                params: {
                  orderCardCode: action.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          }
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  deleteOrderCardPrincipal$: Observable<
    UserActions.DeleteOrderCardAdminPrincipalSuccess | UserActions.DeleteOrderCardAdminPrincipalFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.DeleteOrderCardAdminPrincipal),
      switchMap((action: UserActions.DeleteOrderCardAdminPrincipal) =>
        this.connector.deleteOrderCardPrincipal(action.userId, action.code, action.principal).pipe(
          map((orderCard: OrderCard) => new UserActions.DeleteOrderCardAdminPrincipalSuccess(action.code, orderCard)),
          catchError((error) => of(new UserActions.DeleteOrderCardAdminPrincipalFail(action.code, normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForDeleteOrderCardPrincipal$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.OrderCardsAdminActionTypes.DeleteOrderCardAdminPrincipalFail),
        map((action: UserActions.DeleteOrderCardAdminPrincipalFail) => {
          if (!isErrorGloballyHandled(action.error?.status)) {
            if (action.error?.status === HttpResponseStatus.NOT_FOUND) {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'orderCards.orderCardNotFound_message',
                  params: {
                    orderCardCode: action.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            } else {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'orderCards.removeOrderCardPrincipalFailure_message',
                  params: {
                    orderCardCode: action.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            }
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  updateOrderCardPrincipals$: Observable<
    UserActions.UpdateOrderCardAdminPrincipalsSuccess | UserActions.UpdateOrderCardAdminPrincipalsFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.UpdateOrderCardAdminPrincipals),
      map((action: UserActions.UpdateOrderCardAdminPrincipals) => action.payload),
      switchMap((payload) =>
        this.connector.updateOrderCardPrincipals(payload.userId, payload.code, payload.principals, payload.action).pipe(
          map((orderCard: OrderCard) => new UserActions.UpdateOrderCardAdminPrincipalsSuccess({ ...payload, orderCard })),
          catchError((error) =>
            of(
              new UserActions.UpdateOrderCardAdminPrincipalsFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForUpdateOrderCardPrincipals$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.OrderCardsAdminActionTypes.UpdateOrderCardAdminPrincipalsFail),
        map((action: UserActions.UpdateOrderCardAdminPrincipalsFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            if (action.payload.error?.status === HttpResponseStatus.NOT_FOUND) {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'orderCards.orderCardNotFound_message',
                  params: {
                    orderCardCode: action.payload.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            } else {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'orderCards.updateOrderCardPrincipalsFailure_message',
                  params: {
                    orderCardCode: action.payload.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            }
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  addOrderCardProduct$: Observable<
    UserActions.AddOrderCardAdminProductSuccess | UserActions.AddOrderCardAdminProductFail | GlobalMessageActions.AddMessage
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.AddOrderCardAdminProduct),
      switchMap((action: UserActions.AddOrderCardAdminProduct) =>
        this.connector.addOrderCardProduct(action.userId, action.code, action.product).pipe(
          concatMap((orderCard: OrderCard) => [
            new UserActions.AddOrderCardAdminProductSuccess(action.code, orderCard),
            new GlobalMessageActions.AddMessage({
              text: { key: 'orderCards.addOrderCardProductSuccess_message' },
              type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
            }),
          ]),
          catchError((error) => of(new UserActions.AddOrderCardAdminProductFail(action.code, normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  replaceOrderCardProduct$: Observable<
    | UserActions.ReplaceOrderCardAdminProductSuccess
    | UserActions.ReplaceOrderCardAdminProductFail
    | GlobalMessageActions.AddMessage
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.ReplaceOrderCardAdminProduct),
      switchMap((action: UserActions.ReplaceOrderCardAdminProduct) =>
        this.connector.replaceOrderCardProduct(action.userId, action.code, action.oldProductCode, action.newProductCode).pipe(
          concatMap((orderCard: OrderCard) => [
            new UserActions.ReplaceOrderCardAdminProductSuccess(action.code, orderCard, action.newProductCode),
            new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderCards.replaceOrderCardProductSuccess_message',
                uid: `${orderCard.code}-${action.newProductCode}`,
              },
              type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
            }),
          ]),
          catchError((error) => of(new UserActions.ReplaceOrderCardAdminProductFail(action.code, normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForAddOrderCardProduct$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.AddOrderCardAdminProductFail),
      map((action: UserActions.AddOrderCardAdminProductFail) => {
        if (!isErrorGloballyHandled(action.error?.status)) {
          if (action.error?.status === HttpResponseStatus.NOT_FOUND) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderCards.orderCardNotFound_message',
                params: {
                  orderCardCode: action.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderCards.addOrderCardProductFailure_message',
                params: {
                  orderCardCode: action.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          }
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  addOrderCardArticles$: Observable<
    UserActions.AddOrderCardAdminArticlesSuccess | UserActions.AddOrderCardAdminArticlesFail | GlobalMessageActions.AddMessage
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.AddOrderCardAdminArticles),
      switchMap((action: UserActions.AddOrderCardAdminArticles) =>
        this.connector.addOrderCardArticles(action.userId, action.code, action.articles).pipe(
          concatMap((orderCard: OrderCard) => [
            new UserActions.AddOrderCardAdminArticlesSuccess(action.code, orderCard),
            new GlobalMessageActions.AddMessage({
              text: {
                key:
                  action.articles.length > 1
                    ? 'orderCards.addOrderCardProductsSuccess_message'
                    : 'orderCards.addOrderCardProductSuccess_message',
              },
              type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
            }),
          ]),
          catchError((error) => of(new UserActions.AddOrderCardAdminArticlesFail(action.code, normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForAddOrderCardArticles$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.AddOrderCardAdminArticlesFail),
      map((action: UserActions.AddOrderCardAdminArticlesFail) => {
        if (!isErrorGloballyHandled(action.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'orderCards.addOrderCardProductsFailure_message',
              params: {
                orderCardCode: action.code,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  deleteOrderCardProduct$: Observable<
    UserActions.DeleteOrderCardAdminProductSuccess | UserActions.DeleteOrderCardAdminProductFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.DeleteOrderCardAdminProduct),
      switchMap((action: UserActions.DeleteOrderCardAdminProduct) =>
        this.connector.deleteOrderCardProduct(action.userId, action.code, action.product).pipe(
          map((orderCard: OrderCard) => new UserActions.DeleteOrderCardAdminProductSuccess(action.code, orderCard)),
          catchError((error) => of(new UserActions.DeleteOrderCardAdminProductFail(action.code, normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForDeleteOrderCardProduct$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.OrderCardsAdminActionTypes.DeleteOrderCardAdminProductFail),
        map((action: UserActions.DeleteOrderCardAdminProductFail) => {
          if (!isErrorGloballyHandled(action.error?.status)) {
            if (action.error?.status === HttpResponseStatus.NOT_FOUND) {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'orderCards.orderCardNotFound_message',
                  params: {
                    orderCardCode: action.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            } else {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'orderCards.removeOrderCardProductFailure_message',
                  params: {
                    orderCardCode: action.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            }
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  updateOrderCardProducts$: Observable<
    UserActions.UpdateOrderCardAdminProductsSuccess | UserActions.UpdateOrderCardAdminProductsFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.UpdateOrderCardAdminProducts),
      map((action: UserActions.UpdateOrderCardAdminProducts) => action.payload),
      switchMap((payload) =>
        this.connector.updateOrderCardProducts(payload.userId, payload.code, payload.products, payload.action).pipe(
          map((orderCard: OrderCard) => new UserActions.UpdateOrderCardAdminProductsSuccess({ ...payload, orderCard })),
          catchError((error) =>
            of(
              new UserActions.UpdateOrderCardAdminProductsFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForUpdateOrderCardProducts$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.OrderCardsAdminActionTypes.UpdateOrderCardAdminProductsFail),
        map((action: UserActions.UpdateOrderCardAdminProductsFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            if (action.payload.error?.status === HttpResponseStatus.NOT_FOUND) {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'orderCards.orderCardNotFound_message',
                  params: {
                    orderCardCode: action.payload.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            } else {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'orderCards.updateOrderCardProductsFailure_message',
                  params: {
                    orderCardCode: action.payload.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            }
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  loadOrderCardAdminConfigurations$: Observable<
    | UserActions.LoadOrderCardAdminConfigurationsSuccess
    | UserActions.LoadOrderCardAdminConfigurationsFail
    | UserActions.LoadOrderCardAdminConfigurationSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.LoadOrderCardAdminConfigurations),
      mergeMap((action: UserActions.LoadOrderCardAdminConfigurations) =>
        this.connector.getOrderCardAdminConfigurations(action.userId, action.code, action.customer).pipe(
          switchMap((data) => [
            new UserActions.LoadOrderCardAdminConfigurationsSuccess(action.code, data),
            ...data.map((config) => new UserActions.LoadOrderCardAdminConfigurationSuccess(action.code, config.product, config)),
          ]),
          catchError((error) => of(new UserActions.LoadOrderCardAdminConfigurationsFail(action.code, normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  loadOrderCardAdminConfiguration$: Observable<
    UserActions.LoadOrderCardAdminConfigurationSuccess | UserActions.LoadOrderCardAdminConfigurationFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.LoadOrderCardAdminConfiguration),
      mergeMap((action: UserActions.LoadOrderCardAdminConfiguration) =>
        this.connector.getOrderCardAdminConfiguration(action.userId, action.code, action.productCode, action.customer).pipe(
          map((data) => new UserActions.LoadOrderCardAdminConfigurationSuccess(action.code, action.productCode, data)),
          catchError((error) =>
            of(new UserActions.LoadOrderCardAdminConfigurationFail(action.code, action.productCode, normalizeHttpError(error)))
          )
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  updateOrderCardAdminConfiguration$: Observable<
    UserActions.UpdateOrderCardAdminConfigurationSuccess | UserActions.UpdateOrderCardAdminConfigurationFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.UpdateOrderCardAdminConfiguration),
      switchMap((action: UserActions.UpdateOrderCardAdminConfiguration) =>
        this.connector.updateOrderCardAdminConfiguration(action.userId, action.code, action.customer, action.params).pipe(
          map((data) => new UserActions.UpdateOrderCardAdminConfigurationSuccess(action.code, action.productCode, data)),
          catchError((error) =>
            of(new UserActions.UpdateOrderCardAdminConfigurationFail(action.code, action.productCode, normalizeHttpError(error)))
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForUpdateOrderCardAdminConfiguration$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.OrderCardsAdminActionTypes.UpdateOrderCardAdminConfigurationFail),
        map((action: UserActions.UpdateOrderCardAdminConfigurationFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderCards.updateOrderCardConfigurationFailure_message',
                params: {
                  orderCardCode: action.payload.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
    );

  loadOrderCardAvailableContactPersons$: Observable<
    UserActions.LoadAvailableContactPersonsSuccess | UserActions.LoadAvailableContactPersonsFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsAdminActionTypes.LoadAvailableContactPersons),
      concatMap((action: UserActions.LoadAvailableContactPersons) =>
        this.b2bUserConnector
          .getB2BUsers(action.payload.userId, {
            key: 'usersList',
            roles: action.payload.roles,
          })
          .pipe(
            map((org: OrgUnitUserPage) => {
              const results = org.results || [];
              return new UserActions.LoadAvailableContactPersonsSuccess(results);
            }),
            catchError((error: HttpErrorResponse) =>
              of(new UserActions.LoadAvailableContactPersonsFail(normalizeHttpError(error)))
            )
          )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForLoadAvailableContactPersons$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.OrderCardsAdminActionTypes.LoadAvailableContactPersonsFail),
        map((action: UserActions.LoadAvailableContactPersonsFail) => {
          if (!isErrorGloballyHandled(action.payload.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderCards.loadAvailableContactPersonsFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
    );

  constructor(
    private actions$: Actions,
    private connector: OrderCardsAdminConnector,
    private b2bUserConnector: B2BUserConnector
  ) {}
}
